<script setup lang="ts">
import CoreIcon from '@core/core-icon.vue';
import { computed, ref } from 'vue';
const props = defineProps({
	type: {
		type: String,
		default: 'text'
	},
	name: {
		type: String,
		default: null
	},
	placeholder: {
		type: String,
		default: ''
	},
	label: {
		type: String,
		default: null
	},
	error: {
		type: Boolean,
		default: false
	},
	success: {
		type: Boolean,
		default: false
	},
	password: {
		type: Boolean,
		default: false
	},
	disabled: {
		type: Boolean,
		default: false
	},
	labelFont: {
		type: String,
		default: 'font-18-pro-medium'
	}
});

const passwordActive = ref(true);

const onChangeValue = () => {
	document.activeElement?.blur();
}
const togglePassword = () => {
	passwordActive.value = !passwordActive.value;
};

const modelValue = defineModel();

const inputType = computed(() => {
	if (props.password) {
		return passwordActive.value ? 'password' : props.type;
	}
	return props.type;
});
const modes = computed(() => ({
	'form-field_password': props.password
}))
</script>
<template>
	<label class="form-field" :class="modes">
		<!-- todo: move checkbox to another component -->
		<slot name="label"
			v-if="label && type!=='checkbox'"
		>
			<span
				class="form-field__label"
				:class="labelFont"
				v-html="label"
			/>
		</slot>

		<input
			:type="inputType"
			:name="name"
			:placeholder="placeholder"
			:disabled="disabled"
			v-model="modelValue"
			v-on:change="onChangeValue"
			:class="{'form-field__input': true, 'form-field__input--error': error}"
		/>

		<span
			v-if="password"
			class="form-field__password"
			:class="{'is-active': passwordActive && !disabled}"
			@click="togglePassword"
		>
			<span v-if="!disabled" class="form-field__password-icon form-field__password-icon_default">
				<core-icon
					name="eye"
					viewBox="0 0 24 14"
				/>
			</span>
			<span v-if="!disabled" class="form-field__password-icon form-field__password-icon_closed">
				<core-icon
					name="eye-closed"
					viewBox="0 0 28 28"
				/>
			</span>
		</span>

		<span
			v-if="success"
			class="form-field__icon"
		>
			<core-icon
				name="checkmark-circle"
				viewBox="0 0 16 16"
			/>
		</span>

		<slot name="label"
			v-if="label && type=='checkbox'"
		>
			<span
				class="form-field__label font-18-pro-medium form-field__label--checkbox"
				v-html="label"
			/>
		</slot>
	</label>
</template>

<style lang="scss">
	.form-field {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		width: 100%;
		position: relative;

		--input-height: 5.2rem;

		@include min-desktop {
			--input-height: 6.2rem;
		}

		&:last-child {
			margin-bottom: 0;
		}

		&_password {
			.form-field {
				&__input {
					padding-right: 5.6rem!important;
				}
			}
		}

		&__icon {
			position: absolute;
			right: 0;
			bottom: 0;
			height: var(--input-height);
			width: 5.6rem;
			pointer-events: none;
			display: flex;
			align-items: center;
			justify-content: center;
			// stroke: currentColor;
			stroke: var(--color-active);
			fill: transparent;
			svg {
				// opacity: 0.5;
				width: 1.8rem;
			}
		}

		&__password {
			position: absolute;
			right: 0;
			bottom: 0;
			height: var(--input-height);
			width: 5.6rem;
			cursor: pointer;
			fill: currentColor;
			&.is-active {
				.form-field {
					&__password-icon {
						&_default {
							svg {
								transform: scale(0.2);
								opacity: 0;
							}
						}
						&_closed {
							svg {
								transform: scale(1);
								opacity: 1;
							}
						}
					}
				}
			}
		}

		&__password-icon {
			position: absolute;
			left: 0;
			top: 0;
			width: 100%;
			height: 100%;
			display: flex;
			align-items: center;
			justify-content: center;
			opacity: 0.5;

			svg {
				width: 2.4rem;
				transition: opacity 0.15s, transform 0.15s;
			}

			&_closed {
				svg {
					transform: scale(0.2);
					opacity: 0;
				}
			}
		}

		&__label {
			// color: var(--color-text);
			&.font-regular {
				margin-bottom: 1rem;
				@include min-desktop {
					@include font-16-pro-regular;
				}
			}

			&.font-18-pro-medium {
				margin-bottom: 1.6rem;
				@include min-desktop {
					@include font-24-pro-bold;
				}
			}

			&--checkbox {
				margin-bottom: 0;
			}

			a {
				text-decoration: underline;
			}
		}

		&__input {
			display: block;
			transition: border-color ease 0.2s;

			&[type="checkbox"]{
				width: 1.6rem;
				height: 1.6rem;
				border: 1px solid #DDDCDB;
				background: #fff;
				margin-right: 1.6rem;
				display: inline-block;
				vertical-align: middle;
				position: relative;

				&:checked {
					background: #DDDCDB;
					border-color: #DDDCDB;
				}
				&:checked:after {
					content: '';
					position: absolute;
					top: 50%;
				}
			}

			&:not([type="checkbox"]) {
				width: 100%;
				border-radius: 1.6rem;
				border: 1px solid rgba(#000, 0.25);
				padding: 0 1.6rem;
				height: var(--input-height);
				color: var(--color-text);

				@include theme-dark {
					background-color: transparent;
					border-color: rgba(var(--color-active-rgb), 0.25);
					&:focus {
						border-color: var(--color-active);
					}
					&:hover {
						border-color: var(--color-active);
					}
				}
			}

			&::placeholder {
				color: var(--color-text);
				opacity: 0.5;
			}

			&:not(:disabled) {
				&:hover {
					border-color: rgba(#000, 0.6);
				}
				&:focus {
					border-color: rgba(#000, 0.6);
				}
			}

			&:disabled {
        opacity: .4;
			}

			&--error {
				border: 2px solid var(--color-red) !important;
			}
		}
	}
</style>
